import { Descope, useSession } from '@descope/react-sdk';
import { Box } from '@mui/system';
import analytics, { AnalyticsActionName } from '@utils/analytics';
import { getLastRoute, resetLastRoute } from '@utils/routes';
import { APP_ROUTES } from 'constants/routes';
import { useNavigate } from 'hooks';
import React, { FC, useEffect } from 'react';
import LoginPageBackground from './LoginPageBackground';

const LoginPage: FC = () => {
	const navigate = useNavigate();
	const { isAuthenticated } = useSession();

	useEffect(() => {
		if (isAuthenticated) {
			navigate(getLastRoute() || APP_ROUTES.default);
		}
	}, [navigate, isAuthenticated]);

	const handleSuccess = React.useCallback(
		(res: CustomEvent) => {
			const lastRoute = getLastRoute();
			const firstSeen =
				res.detail.firstSeen ||
				!res.detail.user ||
				!res.detail.user.userTenants ||
				res.detail.user.userTenants?.length === 0;
			analytics().trackUserResponse(
				firstSeen ? AnalyticsActionName.signUp : AnalyticsActionName.signIn,
				res.detail.user
			);
			navigate(
				firstSeen ? APP_ROUTES.gettingStarted : lastRoute || APP_ROUTES.default,
				{
					state: { firstSeen }
				}
			);
			try {
				if (lastRoute) resetLastRoute();
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error('failed to set local storage', e);
			}
		},
		[navigate]
	);

	return (
		<Box
			data-testid="login-page"
			id="login-page"
			display="flex"
			flexGrow={1}
			flexDirection="column"
			justifyContent="center"
			alignItems="center"
			style={{
				background: 'rgba(0, 0, 0, 0.3)'
			}}
		>
			<LoginPageBackground />
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				id="login-container"
				data-testid="login-container"
				sx={{
					gap: 24,
					position: 'relative',
					width: {
						xs: '90%',
						sm: 450
					},
					backgroundColor: '#ffffff',
					boxShadow: `0px 10px 13px -6px rgba(0, 0, 0, 0.2), 0px 20px 31px 3px rgba(0, 0, 0, 0.14), 0px 8px 38px 7px rgba(0, 0, 0, 0.12)`,
					borderRadius: 3
				}}
			>
				<Descope
					flowId="sign-up-or-in"
					onSuccess={handleSuccess}
					theme="light"
				/>
			</Box>
		</Box>
	);
};

export default LoginPage;
